import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Flame, Sparkles, BookOpen, Layers, ClipboardList, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export default function Dashboard() {
  const { user } = useAuth();
  const [streak, setStreak] = useState<{ current_streak: number; longest_streak: number; total_xp: number } | null>(null);
  const [dueCount, setDueCount] = useState(0);
  const [recentCourse, setRecentCourse] = useState<{ slug: string; title: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: s } = await supabase.from("streaks").select("*").eq("user_id", user.id).maybeSingle();
      setStreak(s as any);
      const { count } = await supabase
        .from("flashcard_reviews")
        .select("id", { head: true, count: "exact" })
        .eq("user_id", user.id)
        .lte("due_at", new Date().toISOString());
      setDueCount(count || 0);
      const { data: c } = await supabase.from("courses").select("slug,title").eq("published", true).order("sort_order").limit(1).maybeSingle();
      setRecentCourse(c as any);
    })();
  }, [user]);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold">こんにちは 👋</h1>
        <p className="text-muted-foreground">Pick up where you left off.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm">Current streak</CardTitle>
            <Flame className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{streak?.current_streak ?? 0} days</div>
            <p className="text-xs text-muted-foreground">Longest: {streak?.longest_streak ?? 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm">Total XP</CardTitle>
            <Sparkles className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{streak?.total_xp ?? 0}</div>
            <p className="text-xs text-muted-foreground">Keep learning to level up</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm">Cards due</CardTitle>
            <Layers className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{dueCount}</div>
            <Button asChild size="sm" className="mt-2" disabled={!dueCount}>
              <Link to="/decks">Review now</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ActionCard to="/courses" icon={BookOpen} title="Courses" desc="Structured JLPT lessons" />
        <ActionCard to="/decks" icon={Layers} title="Flashcards" desc="Spaced-repetition vocab" />
        <ActionCard to="/tests" icon={ClipboardList} title="Tests" desc="Practice quizzes" />
        <ActionCard to="/dictionary" icon={Search} title="Dictionary" desc="Search any word" />
      </div>

      {recentCourse && (
        <Card>
          <CardHeader>
            <CardTitle>Continue learning</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div>
              <div className="font-medium">{recentCourse.title}</div>
              <div className="text-sm text-muted-foreground">Jump back into your course</div>
            </div>
            <Button asChild>
              <Link to={`/courses/${recentCourse.slug}`}>Open</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ActionCard({ to, icon: Icon, title, desc }: any) {
  return (
    <Link to={to}>
      <Card className="hover:shadow-md transition-shadow h-full">
        <CardContent className="p-4 space-y-2">
          <Icon className="h-6 w-6 text-primary" />
          <div className="font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground">{desc}</div>
        </CardContent>
      </Card>
    </Link>
  );
}
